import { useEffect, useMemo, useState } from 'react';
import { Box, Card, Divider, ListItem } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { CompanySelect, ListHeader, ListRow, LoanSelect, ToolbarContainer, dataModelIcons } from '/src/components';
import { useFilterStore, useStatementStore, useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Company, Loan } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';

/**
 * List Statements Page
 */
export const ListStatementsPage = observer(() => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = true;
  const rowHeight = 48;

  /** Context **/
  const statementStore = useStatementStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [company, setCompany] = useState<Company>();
  const [loan, setLoan] = useState<Partial<Loan>>();

  /** Computed **/
  const statements = statementStore.statements;

  const companies = useMemo(
    () => filterStore.companyListGet().filter((company) => !company.isLender),
    [filterStore._companyList]
  );
  const loans = useMemo(
    () => filterStore.loanListGet().filter((loan) => (company ? loan.borrower._id === company._id : true)),
    [filterStore._loanList, company]
  );

  const listData = useMemo(() => {
    if (statements) {
      const list = statements.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (list && sortReverse) list.reverse();

      return list;
    }
  }, [statements, sortType, sortReverse]);

  /** Methods **/

  /**
   * Sets sortType and sortReverse based on given sortType.
   */
  const sortList = (type: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (loan?._id) statementStore.queryLoanStatements(loan._id);

    return () => (statementStore.statements = undefined);
  }, [loan]);

  /** Render **/
  return (
    <DesktopLayout>
      <Box id="listStatements" component="md-content" className="md-default-theme">
        <Card component="md-card">
          <ToolbarContainer>
            <Box display="flex" flex={33} />

            <Box justifyContent="center" alignItems="center" display="flex">
              {dataModelIcons[DataModelType.Statement]}
              <Box component="span" className="md-title pad-left-xs">
                {`${userStore.isAdmin ? 'Deal ' : ''} Statements`}
              </Box>
            </Box>

            <Box display="flex" flex={33} />
          </ToolbarContainer>

          <Box bgcolor="white">
            <Box display="flex" justifyContent="center" alignItems="center" gap="32px" paddingX="16px">
              <CompanySelect
                value={company?._id ?? ''}
                setValue={(companyId) => {
                  const company = companies.find((company) => company._id === companyId);
                  setCompany(company);
                  setLoan(undefined);
                }}
                companies={companies}
                ariaLabel="Select a Company to view its Statements."
                placeholder="Select Company"
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />

              <LoanSelect
                value={loan?._id ?? ''}
                setValue={(loanId) => {
                  const loan = loans.find((loan) => loan._id === loanId);
                  setLoan(loan);
                  if (!loan) statementStore.statements = undefined;
                }}
                loans={loans}
                ariaLabel="Select a Deal"
                placeholder="All Deals"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />
            </Box>
          </Box>

          {(loan || !!listData?.length) && <Divider />}

          {/* LIST */}
          {loan && !!listData?.length && (
            <Box bgcolor="white">
              <ListHeader
                items={[{ propName: 'effectiveDate', label: 'Statement Date' }]}
                sortType={sortType}
                sortReverse={sortReverse}
                sortList={sortList}
              />
              <Divider />
              <Box
                // className={listData.length > 11 ? 'fixed-list-lg' : ''}
                sx={{ maxHeight: `${(11 * rowHeight).toString()}px` }}
              >
                {listData?.map((statement, i) => (
                  <ListRow
                    key={statement._id}
                    items={[
                      {
                        propName: 'effectiveDate',
                        content: dayjs(statement.effectiveDate).format('MMMM YYYY'),
                        url: `/statements/${loan?._id}/${dayjs(statement.effectiveDate).format('YYYY-M')}`,
                        linkTarget: '_blank',
                      },
                    ]}
                    rowIndex={i}
                  />
                ))}
              </Box>
            </Box>
          )}

          {loan && listData && !listData.length && (
            <ListItem sx={{ bgcolor: 'white' }}>
              <Box
                display="flex"
                flex="100"
                justifyContent="center"
                alignItems="center"
                className="pad-top-bottom md-title thin"
              >
                No Statements Found.
              </Box>
            </ListItem>
          )}
        </Card>
      </Box>
    </DesktopLayout>
  );
});
